/**
 * Convert roof tilt degrees to pitch ratio (X/12)
 * @param tiltDegrees - Roof tilt in degrees
 * @returns Pitch ratio (e.g., 4.5 for 4.5/12 pitch)
 */
export function tiltToPitchRatio(tiltDegrees: number): number {
  const radians = (tiltDegrees * Math.PI) / 180;
  const rise = Math.tan(radians) * 12;
  return Math.round(rise * 10) / 10;
}

/**
 * Convert azimuth degrees to cardinal direction
 * @param azimuth - Azimuth in degrees (0-360)
 * @returns Cardinal direction (N, E, S, W)
 */
export function azimuthToDirection(azimuth: number): string {
  if (azimuth < 45 || azimuth >= 315) return "N";
  if (azimuth < 135) return "E";
  if (azimuth < 225) return "S";
  return "W";
}

/**
 * Get detailed orientation description
 * @param azimuth - Azimuth in degrees
 * @returns Formatted orientation string (e.g., "North 15°")
 */
export function getOrientationLabel(azimuth: number): string {
  const direction = azimuthToDirection(azimuth);
  return `${getFullDirection(direction)} ${Math.round(azimuth)}°`;
}

function getFullDirection(dir: string): string {
  const map: Record<string, string> = {
    N: "North",
    E: "East",
    S: "South",
    W: "West",
  };
  return map[dir] || dir;
}

/**
 * Generate static map URL for roof preview.
 * We proxy through a backend edge function so Google never sees the browser origin.
 */
export function getStaticMapUrl(
  lat: number,
  lng: number,
  zoom: number = 20,
  size: string = "600x400",
): string {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  // Supabase edge function `static-map-image` (public)
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
    zoom: String(zoom),
    size,
  });
  return `${baseUrl}/functions/v1/static-map-image?${params.toString()}`;
}
